/**
 * Help & Capability document parsing.
 *
 * Single source of truth: `docs/help/yap-pro-helpdoc.md` — the live, machine
 * generated Help & Capability document. The structured JSON block (§4.3) is
 * extracted programmatically from the command registry at doc generation
 * time, so parsing it here keeps the Help Center in sync with the registry
 * without a second data copy.
 */

import helpDocRaw from '../../../docs/help/yap-pro-helpdoc.md?raw';

// ---------------------------------------------------------------------------
// Public data model
// ---------------------------------------------------------------------------

export type HelpShortcutStatus = 'DEFAULT' | 'MODIFIED' | 'CUSTOM_ASSIGNED' | 'NO_SHORTCUT';

export type HelpCommandSource = 'registry' | 'contextual' | 'canonical';

export interface HelpCommand {
    id: string;
    name: string;
    description: string;
    /** Sidebar category id (registry/contextual use their JSON category, canonical use `command-line`). */
    categoryId: string;
    defaultShortcut: string | null;
    activeShortcut: string | null;
    shortcutStatus: HelpShortcutStatus;
    uiPath: string;
    scope: string;
    mutatesData: boolean;
    source: HelpCommandSource;
    /** 2D/3D command line spellings (canonical commands only). */
    aliases?: string[];
    /** Optional DOM element id the "Go" action should scroll to and highlight. */
    targetElementId?: string;
}

export interface HelpFeature {
    /** Document section number, e.g. `2.2` or `2.0.3`. */
    id: string;
    title: string;
    summary: string;
    trigger?: string;
    sideEffects?: string;
    parameters?: string;
    edgeCases?: string;
    /** Sidebar category ids this feature belongs to. */
    categoryIds: string[];
    /** Extra labelled paragraphs that don't map to a known field. */
    extras: Array<{ label: string; text: string }>;
}

export interface HelpKeyBinding {
    key: string;
    context: string;
    behavior: string;
}

export interface HelpMcpTool {
    name: string;
    description: string;
    mutatesData: boolean;
}

export interface HelpReleaseNote {
    kind: 'added' | 'changed' | 'removed';
    title: string;
    text: string;
}

export interface HelpCategory {
    id: string;
    label: string;
    group: 'browse' | 'commands' | 'reference';
    commandCount: number;
}

export interface HelpDocument {
    meta: {
        app: string;
        version: string;
        generatedAt: string;
        workbenches: string[];
    };
    features: HelpFeature[];
    commands: HelpCommand[];
    keyBindings: HelpKeyBinding[];
    mcpTools: HelpMcpTool[];
    releaseNotes: HelpReleaseNote[];
    categories: HelpCategory[];
}

export interface HelpSearchResults {
    commands: HelpCommand[];
    features: HelpFeature[];
    keyBindings: HelpKeyBinding[];
    mcpTools: HelpMcpTool[];
    releaseNotes: HelpReleaseNote[];
    totalMatches: number;
}

// ---------------------------------------------------------------------------
// Sidebar categories
// ---------------------------------------------------------------------------

/** Pseudo categories that group document sections without registry commands. */
export const FUNDAMENTALS_CATEGORY_ID = 'fundamentals';
export const COMMAND_LINE_CATEGORY_ID = 'command-line';
export const WHATS_NEW_CATEGORY_ID = 'whats-new';
export const KEY_BINDINGS_CATEGORY_ID = 'key-bindings';
export const MCP_TOOLS_CATEGORY_ID = 'mcp-tools';
export const OVERVIEW_CATEGORY_ID = 'all';

const COMMAND_CATEGORY_ORDER = [
    'File',
    'Edit',
    'Architecture',
    'Structure',
    'Steel',
    'Precast',
    'Systems',
    'Insert',
    'Annotate',
    'Analyze',
    'Collaborate',
    'View',
    'Massing & Site',
    'Modify',
    'Modify (contextual)',
    'Wall Tool',
    'Selection',
    'Properties',
    'Drafting',
    '3D Modeling',
    'Manage',
    'Settings',
];

/** Feature section number → sidebar command category ids it documents. */
const SECTION_CATEGORY_MAP: Record<string, string[]> = {
    '2.0': [FUNDAMENTALS_CATEGORY_ID],
    '2.0.1': [FUNDAMENTALS_CATEGORY_ID],
    '2.0.2': [FUNDAMENTALS_CATEGORY_ID],
    '2.0.3': [FUNDAMENTALS_CATEGORY_ID],
    '2.0.4': [FUNDAMENTALS_CATEGORY_ID],
    '2.0.5': [FUNDAMENTALS_CATEGORY_ID],
    '2.0.6': [FUNDAMENTALS_CATEGORY_ID],
    '2.1': ['File', 'Edit'],
    '2.2': ['Architecture'],
    '2.3': ['Structure'],
    '2.4': ['Steel'],
    '2.5': ['Precast'],
    '2.6': ['Systems'],
    '2.7': ['Insert'],
    '2.8': ['Annotate'],
    '2.9': ['Analyze'],
    '2.10': ['Collaborate'],
    '2.11': ['View'],
    '2.12': ['Massing & Site'],
    '2.13': ['Modify', 'Wall Tool', 'Selection', 'Properties', 'Edit'],
    '2.14': ['Drafting', '3D Modeling'],
    '2.15': ['Manage', 'Settings'],
    '2.16': ['Modify (contextual)'],
    '2.17': [COMMAND_LINE_CATEGORY_ID],
};

// ---------------------------------------------------------------------------
// Raw JSON payload (§4.3) shapes — snake_case as emitted by the doc generator
// ---------------------------------------------------------------------------

interface HelpJsonCommand {
    id: string;
    name: string;
    description: string;
    category?: string;
    default_shortcut?: string | null;
    active_shortcut?: string | null;
    shortcut_status?: string;
    ui_path?: string;
    scope?: string;
    mutates_data?: boolean;
    target?: string;
    target_element_id?: string;
    command_line_names?: { '2d'?: string[]; '3d'?: string[] };
}

interface HelpJsonPayload {
    app?: string;
    version?: string;
    generated_at?: string;
    workbenches?: string[];
    mcp_tools?: Array<{ name: string; description: string; mutates_data?: boolean }>;
    registry_commands?: HelpJsonCommand[];
    contextual_commands?: HelpJsonCommand[];
    canonical_commands?: HelpJsonCommand[];
}

// ---------------------------------------------------------------------------
// Markdown parsing helpers
// ---------------------------------------------------------------------------

const JSON_HEADING_MARKER = '### 4.3';
const FEATURES_HEADING_MARKER = '## 🛠 2.';
const SHORTCUTS_HEADING_MARKER = '## ⌨️ 3.';
const RELEASE_NOTES_HEADING_MARKER = '## 🚀 1.';
const SECTION_HEADING_RE = /^#### (\d+\.\d+(?:\.\d+)?)\s+(.+)$/gm;
const BULLET_RE = /^- \*\*(.+?):\*\*\s*(.+)$/gm;
const TABLE_ROW_RE = /^\|(.+)\|$/gm;

function decodeEntities(text: string): string {
    return text
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
}

/** Strip inline markdown emphasis (`**bold**`, `*italic*`, `` `code` ``) for plain UI text. */
function cleanInlineMarkdown(text: string): string {
    return decodeEntities(
        text
            .replace(/`/g, '')
            .replace(/\*\*([^*]+)\*\*/g, '$1')
            .replace(/\*([^*]+)\*/g, '$1'),
    ).trim();
}

function cleanCell(text: string): string {
    return cleanInlineMarkdown(text).replace(/\s+/g, ' ');
}

/** First prose paragraph of a section block — fallback summary for sections
 *  that describe themselves without a `**Ne işe yarar:**` bullet. */
function firstProseParagraph(body: string): string {
    for (const line of body.split('\n')) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        if (trimmed.startsWith('#') || trimmed.startsWith('|') || trimmed.startsWith('```')) continue;
        if (trimmed.startsWith('- ') || /^\d+\.\s/.test(trimmed)) continue;
        return trimmed;
    }
    return '';
}

function parseEmbeddedJson(markdown: string): HelpJsonPayload | null {
    const headingIndex = markdown.indexOf(JSON_HEADING_MARKER);
    if (headingIndex < 0) return null;
    const fenceStart = markdown.indexOf('```json', headingIndex);
    if (fenceStart < 0) return null;
    const bodyStart = fenceStart + '```json'.length;
    const bodyEnd = markdown.indexOf('```', bodyStart);
    if (bodyEnd < 0) return null;
    try {
        return JSON.parse(markdown.slice(bodyStart, bodyEnd)) as HelpJsonPayload;
    } catch (error) {
        console.error('[HelpCenter] Failed to parse the embedded help JSON block:', error);
        return null;
    }
}

interface SectionBlock {
    id: string;
    title: string;
    body: string;
}

function collectSectionBlocks(markdown: string, startMarker: string, endMarker: string): SectionBlock[] {
    const startIndex = markdown.indexOf(startMarker);
    if (startIndex < 0) return [];
    const endIndex = markdown.indexOf(endMarker, startIndex);
    const scope = markdown.slice(startIndex, endIndex > 0 ? endIndex : undefined);

    const blocks: SectionBlock[] = [];
    SECTION_HEADING_RE.lastIndex = 0;
    let match: RegExpExecArray | null;
    const anchors: Array<{ id: string; title: string; offset: number }> = [];
    while ((match = SECTION_HEADING_RE.exec(scope)) !== null) {
        anchors.push({ id: match[1], title: match[2].trim(), offset: match.index });
    }
    for (let i = 0; i < anchors.length; i++) {
        const anchor = anchors[i];
        const bodyEnd = i + 1 < anchors.length ? anchors[i + 1].offset : scope.length;
        blocks.push({
            id: anchor.id,
            title: anchor.title,
            body: scope.slice(anchor.offset, bodyEnd),
        });
    }
    return blocks;
}

const BULLET_FIELD_MAP: Record<string, 'summary' | 'trigger' | 'sideEffects' | 'parameters' | 'edgeCases'> = {
    'Ne işe yarar': 'summary',
    'Tetikleme': 'trigger',
    'Ön koşullar & yan etkiler': 'sideEffects',
    'Parametreler': 'parameters',
    'Edge cases': 'edgeCases',
};

function parseFeatures(markdown: string): HelpFeature[] {
    const blocks = collectSectionBlocks(markdown, FEATURES_HEADING_MARKER, SHORTCUTS_HEADING_MARKER);
    const features: HelpFeature[] = [];
    for (const block of blocks) {
        const fields: Record<string, string> = {};
        const extras: HelpFeature['extras'] = [];
        BULLET_RE.lastIndex = 0;
        let bullet: RegExpExecArray | null;
        while ((bullet = BULLET_RE.exec(block.body)) !== null) {
            const label = bullet[1].trim();
            const text = cleanInlineMarkdown(bullet[2]);
            const mapped = BULLET_FIELD_MAP[label];
            if (mapped) {
                if (!fields[mapped]) fields[mapped] = text;
            } else {
                extras.push({ label, text });
            }
        }
        const summary = fields.summary ?? cleanInlineMarkdown(firstProseParagraph(block.body));
        if (!summary) continue;
        const title = cleanInlineMarkdown(block.title.replace(/\s*\*\([^)]*\)\*\s*$/, '').trim());
        features.push({
            id: block.id,
            title,
            summary,
            trigger: fields.trigger,
            sideEffects: fields.sideEffects,
            parameters: fields.parameters,
            edgeCases: fields.edgeCases,
            categoryIds: SECTION_CATEGORY_MAP[block.id] ?? [],
            extras,
        });
    }
    return features;
}

function parseKeyBindings(markdown: string): HelpKeyBinding[] {
    const startIndex = markdown.indexOf('### 3.2');
    if (startIndex < 0) return [];
    const endIndex = markdown.indexOf('### 3.3', startIndex);
    const scope = markdown.slice(startIndex, endIndex > 0 ? endIndex : undefined);
    const bindings: HelpKeyBinding[] = [];
    TABLE_ROW_RE.lastIndex = 0;
    let row: RegExpExecArray | null;
    while ((row = TABLE_ROW_RE.exec(scope)) !== null) {
        const cells = row[1].split('|').map(cleanCell);
        if (cells.length < 3) continue;
        const [key, context, behavior] = cells;
        if (!key || key.startsWith(':') || key === 'Tuş / Jest') continue;
        bindings.push({ key, context: context ?? '', behavior: behavior ?? '' });
    }
    return bindings;
}

const RELEASE_KIND_BY_HEADING: Array<{ marker: string; kind: HelpReleaseNote['kind'] }> = [
    { marker: '### 🟢', kind: 'added' },
    { marker: '### 🟡', kind: 'changed' },
    { marker: '### 🔴', kind: 'removed' },
];

/** Delta-log status badges (`[YENİ - date]`, `[GÜNCELLENDİ]`, `[REMOVED]`…) —
 *  already rendered as colored pills, so stripped from the note text. */
const RELEASE_BADGE_TOKEN_RE =
    /\s*\[(?:YENİ|GÜNCELLENDİ|REMOVED|DEPRECATED|MODIFIED|CUSTOM_ASSIGNED|NO_SHORTCUT|DEFAULT)[^\]]*\]\s*/gi;

function parseReleaseNotes(markdown: string): HelpReleaseNote[] {
    const startIndex = markdown.indexOf(RELEASE_NOTES_HEADING_MARKER);
    if (startIndex < 0) return [];
    const endIndex = markdown.indexOf(FEATURES_HEADING_MARKER, startIndex);
    const scope = markdown.slice(startIndex, endIndex > 0 ? endIndex : undefined);
    const notes: HelpReleaseNote[] = [];
    for (const { marker, kind } of RELEASE_KIND_BY_HEADING) {
        const sectionStart = scope.indexOf(marker);
        if (sectionStart < 0) continue;
        const nextMarkers = RELEASE_KIND_BY_HEADING.map((m) => scope.indexOf(m.marker, sectionStart + marker.length))
            .filter((idx) => idx > 0);
        const sectionEnd = nextMarkers.length > 0 ? Math.min(...nextMarkers) : scope.length;
        const section = scope.slice(sectionStart, sectionEnd);
        const itemRe = /^- (.+)$/gm;
        let item: RegExpExecArray | null;
        while ((item = itemRe.exec(section)) !== null) {
            const boldMatch = item[1].match(/^\*\*(.+?)\*\*\s*(.*)$/);
            if (!boldMatch) continue;
            const text = cleanInlineMarkdown(boldMatch[2])
                .replace(RELEASE_BADGE_TOKEN_RE, ' ')
                .replace(/^\s*[—-]\s*/, '')
                .trim();
            notes.push({
                kind,
                title: cleanInlineMarkdown(boldMatch[1]),
                text,
            });
        }
    }
    return notes;
}

// ---------------------------------------------------------------------------
// Document assembly
// ---------------------------------------------------------------------------

function toHelpCommand(raw: HelpJsonCommand, source: HelpCommandSource): HelpCommand | null {
    if (!raw?.id || !raw.name) return null;
    const status = (
        raw.shortcut_status === 'MODIFIED' ||
        raw.shortcut_status === 'CUSTOM_ASSIGNED' ||
        raw.shortcut_status === 'DEFAULT'
            ? raw.shortcut_status
            : 'NO_SHORTCUT'
    ) as HelpShortcutStatus;
    const aliases =
        source === 'canonical'
            ? Array.from(new Set([...(raw.command_line_names?.['2d'] ?? []), ...(raw.command_line_names?.['3d'] ?? [])]))
            : undefined;
    return {
        id: raw.id,
        name: raw.name,
        description: raw.description ?? '',
        categoryId: source === 'canonical' ? COMMAND_LINE_CATEGORY_ID : (raw.category ?? 'Other'),
        defaultShortcut: raw.default_shortcut ?? null,
        activeShortcut: raw.active_shortcut ?? null,
        shortcutStatus: status,
        uiPath: raw.ui_path ?? '',
        scope: raw.scope ?? '',
        mutatesData: Boolean(raw.mutates_data),
        source,
        aliases,
        targetElementId: raw.target_element_id,
    };
}

function buildCategories(doc: Omit<HelpDocument, 'categories'>): HelpCategory[] {
    const counts = new Map<string, number>();
    for (const command of doc.commands) {
        counts.set(command.categoryId, (counts.get(command.categoryId) ?? 0) + 1);
    }
    const knownFeatureCategories = new Set<string>();
    for (const feature of doc.features) {
        for (const id of feature.categoryIds) {
            if (id !== FUNDAMENTALS_CATEGORY_ID) knownFeatureCategories.add(id);
        }
    }
    const commandCategories = [...counts.keys()]
        .filter((id) => id !== COMMAND_LINE_CATEGORY_ID)
        .sort((a, b) => {
            const indexA = COMMAND_CATEGORY_ORDER.indexOf(a);
            const indexB = COMMAND_CATEGORY_ORDER.indexOf(b);
            if (indexA >= 0 && indexB >= 0) return indexA - indexB;
            if (indexA >= 0) return -1;
            if (indexB >= 0) return 1;
            return a.localeCompare(b);
        });
    const extraCategories = commandCategories.filter((id) => !COMMAND_CATEGORY_ORDER.includes(id));
    const ordered = [...commandCategories.filter((id) => COMMAND_CATEGORY_ORDER.includes(id)), ...extraCategories];
    if (counts.has(COMMAND_LINE_CATEGORY_ID)) ordered.push(COMMAND_LINE_CATEGORY_ID);
    // Keep categories that have commands or a documented feature section (e.g. Precast).
    const visible = ordered.filter((id) => (counts.get(id) ?? 0) > 0 || knownFeatureCategories.has(id));

    return [
        { id: OVERVIEW_CATEGORY_ID, label: 'Overview', group: 'browse', commandCount: 0 },
        { id: FUNDAMENTALS_CATEGORY_ID, label: 'Fundamentals', group: 'browse', commandCount: 0 },
        { id: WHATS_NEW_CATEGORY_ID, label: "What's New", group: 'browse', commandCount: doc.releaseNotes.length },
        ...visible.map((id) => ({
            id,
            label: id === COMMAND_LINE_CATEGORY_ID ? 'Command Line (CAD/3D)' : id,
            group: 'commands' as const,
            commandCount: counts.get(id) ?? 0,
        })),
        { id: KEY_BINDINGS_CATEGORY_ID, label: 'Key Bindings', group: 'reference', commandCount: doc.keyBindings.length },
        { id: MCP_TOOLS_CATEGORY_ID, label: 'MCP Tools', group: 'reference', commandCount: doc.mcpTools.length },
    ];
}

/**
 * Parse the raw markdown help document into the normalized `HelpDocument`.
 * The embedded JSON (§4.3) is authoritative for commands and MCP tools; the
 * markdown sections provide features, key bindings and the release delta.
 */
export function parseHelpDocument(markdown: string): HelpDocument {
    const payload = parseEmbeddedJson(markdown) ?? {};

    const commands: HelpCommand[] = [
        ...(payload.registry_commands ?? []),
        ...(payload.contextual_commands ?? []),
        ...(payload.canonical_commands ?? []),
    ]
        .map((raw, index) => {
            const source: HelpCommandSource =
                index < (payload.registry_commands?.length ?? 0)
                    ? 'registry'
                    : index < (payload.registry_commands?.length ?? 0) + (payload.contextual_commands?.length ?? 0)
                      ? 'contextual'
                      : 'canonical';
            return toHelpCommand(raw, source);
        })
        .filter((command): command is HelpCommand => command !== null);

    const mcpTools: HelpMcpTool[] = (payload.mcp_tools ?? [])
        .filter((tool) => Boolean(tool?.name))
        .map((tool) => ({
            name: tool.name,
            description: tool.description ?? '',
            mutatesData: Boolean(tool.mutates_data),
        }));

    const base: Omit<HelpDocument, 'categories'> = {
        meta: {
            app: payload.app ?? 'yap-pro',
            version: payload.version ?? '',
            generatedAt: payload.generated_at ?? '',
            workbenches: payload.workbenches ?? [],
        },
        features: parseFeatures(markdown),
        commands,
        keyBindings: parseKeyBindings(markdown),
        mcpTools,
        releaseNotes: parseReleaseNotes(markdown),
    };

    return { ...base, categories: buildCategories(base) };
}

// ---------------------------------------------------------------------------
// Filtering (instant search)
// ---------------------------------------------------------------------------

function textMatches(haystack: string | undefined, query: string): boolean {
    return typeof haystack === 'string' && haystack.length > 0 && haystack.toLowerCase().includes(query);
}

function commandMatches(command: HelpCommand, query: string): boolean {
    return (
        textMatches(command.name, query) ||
        textMatches(command.id, query) ||
        textMatches(command.description, query) ||
        textMatches(command.uiPath, query) ||
        textMatches(command.scope, query) ||
        textMatches(command.activeShortcut ?? undefined, query) ||
        textMatches(command.aliases?.join(' '), query)
    );
}

function featureMatches(feature: HelpFeature, query: string): boolean {
    return (
        textMatches(feature.title, query) ||
        textMatches(feature.summary, query) ||
        textMatches(feature.trigger, query) ||
        textMatches(feature.sideEffects, query) ||
        textMatches(feature.parameters, query) ||
        textMatches(feature.edgeCases, query) ||
        feature.extras.some((extra) => textMatches(extra.text, query))
    );
}

function filterByCategory(doc: HelpDocument, categoryId: string): HelpSearchResults {
    if (categoryId === OVERVIEW_CATEGORY_ID || categoryId === WHATS_NEW_CATEGORY_ID) {
        return {
            commands: [],
            features: [],
            keyBindings: [],
            mcpTools: [],
            releaseNotes: categoryId === WHATS_NEW_CATEGORY_ID ? doc.releaseNotes : [],
            totalMatches: 0,
        };
    }
    if (categoryId === KEY_BINDINGS_CATEGORY_ID) {
        return {
            commands: [],
            features: [],
            keyBindings: doc.keyBindings,
            mcpTools: [],
            releaseNotes: [],
            totalMatches: 0,
        };
    }
    if (categoryId === MCP_TOOLS_CATEGORY_ID) {
        return {
            commands: [],
            features: [],
            keyBindings: [],
            mcpTools: doc.mcpTools,
            releaseNotes: [],
            totalMatches: 0,
        };
    }
    if (categoryId === FUNDAMENTALS_CATEGORY_ID) {
        return {
            commands: [],
            features: doc.features.filter((f) => f.categoryIds.includes(FUNDAMENTALS_CATEGORY_ID)),
            keyBindings: [],
            mcpTools: [],
            releaseNotes: [],
            totalMatches: 0,
        };
    }
    return {
        commands: doc.commands.filter((c) => c.categoryId === categoryId),
        features: doc.features.filter((f) => f.categoryIds.includes(categoryId)),
        keyBindings: [],
        mcpTools: [],
        releaseNotes: [],
        totalMatches: 0,
    };
}

/**
 * Category-first filtering with instant search applied on top. An empty query
 * on the Overview category returns empty lists: the UI renders the curated
 * overview instead of dumping 700+ rows.
 */
export function filterHelpDocument(doc: HelpDocument, categoryId: string, rawQuery: string): HelpSearchResults {
    const scoped = filterByCategory(doc, categoryId);
    const query = rawQuery.trim().toLowerCase();
    if (!query) return scoped;

    const commands = scoped.commands.filter((c) => commandMatches(c, query));
    const features = scoped.features.filter((f) => featureMatches(f, query));
    const keyBindings = scoped.keyBindings.filter((b) => keyBindingMatches(b, query));
    const mcpTools = scoped.mcpTools.filter((t) => textMatches(t.name, query) || textMatches(t.description, query));
    const releaseNotes = scoped.releaseNotes.filter(
        (n) => textMatches(n.title, query) || textMatches(n.text, query),
    );

    if (categoryId === OVERVIEW_CATEGORY_ID) {
        // Searching from the overview sweeps every collection.
        if (commands.length === 0) {
            commands.push(...doc.commands.filter((c) => commandMatches(c, query)));
        }
        if (features.length === 0) {
            features.push(...doc.features.filter((f) => featureMatches(f, query)));
        }
        if (keyBindings.length === 0) keyBindings.push(...doc.keyBindings.filter((b) => keyBindingMatches(b, query)));
        if (mcpTools.length === 0) {
            mcpTools.push(...doc.mcpTools.filter((t) => textMatches(t.name, query) || textMatches(t.description, query)));
        }
        if (releaseNotes.length === 0) {
            releaseNotes.push(...doc.releaseNotes.filter((n) => textMatches(n.title, query) || textMatches(n.text, query)));
        }
    }

    return {
        commands,
        features,
        keyBindings,
        mcpTools,
        releaseNotes,
        totalMatches: commands.length + features.length + keyBindings.length + mcpTools.length + releaseNotes.length,
    };
}

function keyBindingMatches(binding: HelpKeyBinding, query: string): boolean {
    return textMatches(binding.key, query) || textMatches(binding.context, query) || textMatches(binding.behavior, query);
}

/** Split a shortcut string into Apple-style key cap labels (`Ctrl+Shift+S` → ['Ctrl','⇧','S']). */
export function parseShortcutChips(shortcut: string | null | undefined): string[] {
    if (!shortcut) return [];
    return shortcut
        .split('+')
        .map((part) => part.trim())
        .filter((part) => part.length > 0)
        .map((part) => {
            switch (part) {
                case 'Shift':
                    return '⇧';
                case 'Alt':
                    return '⌥';
                case 'Meta':
                    return '⌘';
                default:
                    return part;
            }
        });
}

/** Parsed document singleton, produced lazily on first Help Center open. */
let cachedDocument: HelpDocument | null = null;

export function getHelpDocument(): HelpDocument {
    if (!cachedDocument) {
        cachedDocument = parseHelpDocument(helpDocRaw);
    }
    return cachedDocument;
}
